import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, User, Download, Clock, CheckCircle, Loader2, CalendarPlus, Shield, ArrowRight, Sparkles, Zap, CreditCard, Smartphone, Users, AlertCircle, Eye, XCircle } from 'lucide-react';
import { PaymentProviderLogo, getProviderFromMethod } from '../components/PaymentProviderLogo';
import SafeImage from '../components/SafeImage';
import { paymentTotalLabel, isCashRsvp, isCashCollected } from '../lib/paymentDisplay';
import { getOrganizerLogoSrc } from '../lib/defaultBranding';
import { formatEventDateTime, formatEventTimeRange } from '../utils/dateFormat';
import { getTicketDisplayState } from '../lib/ticketDisplayState';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lucide icon registry mirroring ticketDisplayState.icon names.
const STATE_ICONS = { CheckCircle, Clock, Eye, AlertCircle, XCircle, Shield };

function generateICS(event) {
  const dateStr = (event.date || '').replace(/-/g, '');
  const timeStr = (event.time || '00:00').replace(':', '') + '00';
  const dtStart = `${dateStr}T${timeStr}`;
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//VelvetGate//Event//EN',
    'BEGIN:VEVENT', `DTSTART:${dtStart}`, `SUMMARY:${event.title || 'Event'}`,
    `LOCATION:${event.location || ''}`, 'DESCRIPTION:VelvetGate Pass — Present your pass at the door.',
    'END:VEVENT', 'END:VCALENDAR'
  ];
  return lines.join('\r\n');
}

function downloadICS(event) {
  const ics = generateICS(event);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(event.title || 'event').replace(/[^a-zA-Z0-9]/g, '-')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('Calendar event downloaded');
}

export default function TicketDisplay() {
  const { ticket_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const isDemo = searchParams.get('demo') === 'true';
  const [ticket, setTicket] = useState(null);
  const [event, setEvent] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletStatus, setWalletStatus] = useState({ apple_wallet: false, google_wallet: false });
  const [walletLoading, setWalletLoading] = useState(null);
  const [claimCodeCopied, setClaimCodeCopied] = useState(false);

  useEffect(() => { fetchTicketData(); }, [ticket_id]);

  const fetchTicketData = async () => {
    try {
      const [ticketRes, settingsRes] = await Promise.all([
        axios.get(`${API}/tickets/${ticket_id}`),
        axios.get(`${API}/settings/platform`)
      ]);
      const ticketData = ticketRes.data;
      const isDemoTicket = ticketData.is_demo || ticketData.event_id === 'demo';
      const eventEndpoint = isDemoTicket ? `${API}/demo/event` : `${API}/events/${ticketData.event_id}`;
      const [eventRes, walletRes] = await Promise.all([
        axios.get(eventEndpoint),
        isDemoTicket
          ? Promise.resolve({ data: { apple_wallet: false, google_wallet: false } })
          : axios.get(`${API}/tickets/${ticket_id}/wallet/status`).catch(() => ({ data: { apple_wallet: false, google_wallet: false } }))
      ]);
      setTicket(ticketData);
      setEvent(eventRes.data);
      setPlatformSettings(settingsRes.data);
      setWalletStatus(walletRes.data);
    } catch {
      toast.error('Pass not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAppleWallet = async () => {
    if (!walletStatus.apple_wallet) { toast.info('Apple Wallet not yet configured. Download the PDF pass instead.'); return; }
    setWalletLoading('apple');
    try {
      const response = await axios.get(`${API}/tickets/${ticket_id}/wallet/apple`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url; a.download = `VelvetGate-${ticket.ticket_number}.pkpass`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to generate Apple Wallet pass'); } finally { setWalletLoading(null); }
  };

  const handleGoogleWallet = async () => {
    if (!walletStatus.google_wallet) { toast.info('Google Wallet not yet configured. Download the PDF pass instead.'); return; }
    setWalletLoading('google');
    try {
      const response = await axios.get(`${API}/tickets/${ticket_id}/wallet/google`);
      window.open(response.data.save_url, '_blank');
    } catch { toast.error('Failed to generate Google Wallet link'); } finally { setWalletLoading(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!ticket || !event) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-[#C44545] text-lg font-semibold mb-2">Pass Not Found</div>
          <p className="text-[#777] text-sm">This link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  const isGranted = ticket.status === 'access_granted' || ticket.status === 'used';
  const isPending = ticket.status === 'pending_approval';
  const isRevoked = ticket.status === 'revoked';
  const isRefunded = (ticket.payment_status || '').toLowerCase() === 'refunded';
  // ── Iter130: unified lifecycle-aware display state. Resolves
  //    post-event pending → REQUEST EXPIRED, RSVP no-show → DID NOT ATTEND,
  //    cash-unpaid + past → PAYMENT NOT COMPLETED, etc. Replaces the
  //    three hardcoded badge branches that lived here.
  const displayState = getTicketDisplayState(ticket, event, new Date());
  const StatusIcon = STATE_ICONS[displayState.icon] || CheckCircle;
  const isRevokedTone = displayState.tone === 'revoked';
  const entryQty = ticket.entry_quantity || 1;
  const hasAddOns = ticket.purchased_add_ons?.length > 0;
  const showDemoPanel = isDemo || ticket.is_demo || ticket.event_id === 'demo';
  const isLoggedIn = !!localStorage.getItem('session_token');

  const handleCreateEvent = () => {
    // Clear any demo artifacts before navigating
    navigate(isLoggedIn ? '/events/create' : '/login');
  };

  const handleExitDemo = () => {
    navigate('/');
  };
  const { src: organizerLogoSrc, isDefault: organizerLogoIsDefault } = getOrganizerLogoSrc(event);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Demo banner */}
      {showDemoPanel && (
        <div className="bg-[#C6A66B]/10 border-b border-[#C6A66B]/20 py-2 px-4 flex items-center justify-center gap-3" data-testid="demo-confirmation-banner">
          <span className="text-xs font-semibold text-[#C6A66B] tracking-wider uppercase">Demo Mode</span>
          <button onClick={handleExitDemo} className="text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-[#C6A66B]/30 text-[#C6A66B] hover:bg-[#C6A66B]/10 transition-colors" data-testid="exit-demo-button">
            Exit Demo
          </button>
        </div>
      )}

      {/* Status accent bar */}
      <div className={`${isRevoked ? 'h-2' : 'h-1'} ${isPending ? 'bg-[#FFB800]' : isRevoked ? 'bg-[#C44545]' : 'bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F]'}`} />

      <div className={`w-full max-w-[420px] mx-auto px-5 py-10 ${isRevoked ? 'relative' : ''}`}>

        {/* Prominent REVOKED watermark overlay */}
        {isRevoked && (
          <div
            className="absolute inset-0 pointer-events-none flex items-center justify-center z-10"
            aria-hidden="true"
            data-testid="revoked-watermark"
          >
            <div
              className="text-[#C44545]/25 font-black tracking-[0.25em] select-none"
              style={{
                transform: 'rotate(-22deg)',
                fontSize: '5.5rem',
                lineHeight: 1,
                textShadow: '0 0 2px rgba(196,69,69,0.3)',
                letterSpacing: '0.18em',
              }}
            >
              VOID
            </div>
          </div>
        )}

        {/* ── HEADER: Logo ── */}
        <div className="text-center mb-8">
          {platformSettings?.platform_logo_url ? (
            <img
              src={`${BACKEND_URL}${platformSettings.platform_logo_url}`}
              alt="VelvetGate"
              className="w-1/2 max-w-[200px] mx-auto mb-6"
              data-testid="platform-logo-header"
            />
          ) : (
            <div className="text-3xl font-black text-[#C6A66B] tracking-tight mb-6" data-testid="platform-text-header">VELVETGATE</div>
          )}

          {/* ── STATUS BADGE — Iter130 unified, lifecycle-aware ──
              Renders ONE badge driven by getTicketDisplayState. Previously
              this was three hardcoded branches (pending / granted / revoked)
              which left past-pending tickets misleadingly labeled "PENDING".
              The helper now produces tones for all 10 lifecycle states and
              this surface honors them with consistent premium styling. */}
          <div
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border ${displayState.badge} ${isRevokedTone ? 'border-2 shadow-[0_0_20px_rgba(196,69,69,0.3)] px-5 py-3' : ''}`}
            data-testid={`status-badge-${displayState.key}`}
            data-pass-state={displayState.key}
          >
            <StatusIcon className={`w-4 h-4 ${isRevokedTone ? '' : ''}`} />
            <span className={`text-sm font-bold uppercase ${isRevokedTone ? 'font-black tracking-[0.25em]' : 'tracking-wider'}`}>
              {isRevokedTone ? 'Pass Revoked' : displayState.label}
            </span>
          </div>
          {isGranted && ticket.replaces_ticket_id && (
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-sm bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider" data-testid="replacement-pass-chip">
              Replacement pass — use this one
            </div>
          )}
        </div>

        {/* ── EVENT TITLE (dominant) ── */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-black text-[#F3EEE7] tracking-tight leading-tight" data-testid="confirmation-heading">
            {event.title}
          </h1>
          {isPending && (
            <p className="text-sm text-[#B9B1A6] mt-3 leading-relaxed max-w-xs mx-auto">Your request is under review. You'll be notified once access is granted.</p>
          )}
          {isGranted && (
            <p className="text-sm text-[#B9B1A6] mt-3">Present your QR code at the door.</p>
          )}
          {isRevoked && (
            <div className="mt-3 max-w-xs mx-auto" data-testid="revoked-explanation">
              <p className="text-base font-bold text-[#C44545]">This pass is no longer valid.</p>
              {isRefunded ? (
                <p className="text-xs text-[#B9B1A6] mt-2 leading-relaxed" data-testid="refunded-explanation">
                  Your payment was refunded. The refund will appear on your card within a few business days. This pass cannot be used for entry.
                </p>
              ) : (
                <p className="text-xs text-[#B9B1A6] mt-2 leading-relaxed">It cannot be used for entry. If a replacement was issued, use the newest valid pass in your inbox.</p>
              )}
              {ticket.replaced_by_ticket_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/ticket/${ticket.replaced_by_ticket_id}`)}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] text-xs font-bold uppercase tracking-wider transition-colors"
                  data-testid="view-replacement-button"
                >
                  View Replacement Pass
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── QR CODE (priority element) ── */}
        {isGranted && (
          <div className="flex justify-center mb-10" data-testid="qr-section">
            <div className="bg-white p-5 rounded-md shadow-[0_0_40px_rgba(198,166,107,0.10)]">
              <QRCode
                value={`ticket:${ticket.ticket_id}`}
                size={280}
                style={{ width: '100%', height: 'auto', minWidth: '280px', maxWidth: '280px' }}
                data-testid="qr-code"
              />
            </div>
          </div>
        )}

        {/* ── HOST LOGO (near QR) — host-specific or VelvetGate platform default ── */}
        <div className="flex justify-center mb-8">
          <img
            src={organizerLogoSrc}
            alt={organizerLogoIsDefault ? 'VelvetGate' : 'Host'}
            className="max-w-[120px] max-h-[80px] object-contain"
            data-testid={organizerLogoIsDefault ? 'organizer-logo-default' : 'organizer-logo'}
          />
        </div>

        {/* ── SAVE PASS PROMPT (subtle, below QR) ── */}
        {(isGranted || isPending) && !showDemoPanel && (
          <div className="mb-6 mx-auto max-w-[320px]" data-testid="save-pass-prompt">
            <div className="border border-[#1A1A1A] rounded-md p-4 bg-[#0A0A0A]/80 text-center">
              <div className="text-xs font-semibold text-[#C6A66B] tracking-wide mb-2">Save your pass for quick access</div>
              {/iPhone|iPad|iPod/i.test(navigator.userAgent) ? (
                <p className="text-[11px] text-[#777] leading-relaxed">
                  Tap <span className="text-[#B9B1A6] font-medium">Share</span> <span className="inline-block w-3.5 h-3.5 align-middle text-[10px] border border-[#555] rounded text-[#B9B1A6] text-center leading-[14px]">&#8593;</span> then <span className="text-[#B9B1A6] font-medium">Add to Home Screen</span>
                </p>
              ) : (
                <p className="text-[11px] text-[#777] leading-relaxed">
                  Tap <span className="text-[#B9B1A6] font-medium">menu</span> <span className="text-[#B9B1A6] font-medium">(&#8942;)</span> then <span className="text-[#B9B1A6] font-medium">Add to Home Screen</span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── TICKET INFO CARD ── */}
        <div className={`${isRevoked ? 'bg-[#0A0A0A] border-2 border-[#C44545]/40 opacity-60 saturate-50' : 'bg-[#0A0A0A] border border-[#1A1A1A]'} rounded-md overflow-hidden mb-8`} data-testid="confirmation-card">

          {/* Pass number */}
          <div className="px-5 py-4 border-b border-[#1A1A1A] text-center bg-[#0D0D0D]">
            <div className="text-[9px] tracking-[0.3em] text-[#555] uppercase font-semibold">Pass Number</div>
            <div className="text-lg font-mono font-bold text-[#F3EEE7] tracking-wider mt-1" data-testid="ticket-number">{ticket.ticket_number}</div>
            {/* Claim code — view-only display + copy. Hidden when missing so we never render an empty value. */}
            {ticket.claim_code && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0A0A0A] border border-[#1A1A1A]" data-testid="claim-code-row">
                <span className="text-[9px] tracking-[0.3em] text-[#555] uppercase font-semibold">Claim code</span>
                <span className="text-sm font-mono font-semibold text-[#C6A66B] tracking-widest" data-testid="claim-code-value">{ticket.claim_code}</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(ticket.claim_code);
                      setClaimCodeCopied(true);
                      toast.success('Claim code copied');
                      setTimeout(() => setClaimCodeCopied(false), 1600);
                    } catch {
                      toast.error('Could not copy');
                    }
                  }}
                  className="text-[10px] uppercase tracking-wider text-[#7A7468] hover:text-[#C6A66B] transition-colors px-1.5 py-0.5 rounded-sm border border-[#1A1A1A] hover:border-[#C6A66B]/40"
                  aria-label="Copy claim code"
                  data-testid="claim-code-copy"
                >
                  {claimCodeCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
          </div>

          {/* Primary info — stacked, centered */}
          <div className="px-5 py-5 space-y-5 border-b border-[#1A1A1A]">
            {/* Guest Name */}
            <div className="text-center">
              <div className="text-[9px] tracking-[0.3em] text-[#777] uppercase font-semibold">Name(s)</div>
              <div className="text-base font-bold text-[#F3EEE7] mt-1" data-testid="guest-name">{ticket.guest_display_name || ticket.guest_name}</div>
            </div>

            {/* Pass Type */}
            {ticket.pass_tier_name && (
              <div className="text-center">
                <div className="text-[9px] tracking-[0.3em] text-[#777] uppercase font-semibold">Pass</div>
                <div className="text-base font-semibold text-[#F3EEE7] mt-1" data-testid="pass-type">
                  {ticket.pass_tier_name}{entryQty > 1 ? ` x${entryQty}` : ''}
                  {ticket.is_comp && <span className="ml-2 text-[10px] text-[#C6A66B] font-bold tracking-wider">COMP</span>}
                </div>
                {ticket.attendees_count > 1 && (
                  <div className="text-xs text-[#B9B1A6] mt-1 flex items-center justify-center gap-1">
                    <Users className="w-3 h-3" />{ticket.attendees_count} attendees
                  </div>
                )}
              </div>
            )}

            {/* Date & Time */}
            <div className="text-center">
              <div className="text-[9px] tracking-[0.3em] text-[#777] uppercase font-semibold">Date & Time</div>
              <div className="text-sm text-[#F3EEE7] mt-1 flex items-center justify-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#C6A66B]" />
                {formatEventTimeRange(event)}
              </div>
            </div>

            {/* Location */}
            <div className="text-center">
              <div className="text-[9px] tracking-[0.3em] text-[#777] uppercase font-semibold">Location</div>
              <div className="text-sm text-[#F3EEE7] mt-1 flex items-center justify-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#C6A66B]" />
                {(() => {
                  // Ticket-aware revealed location from /api/tickets/{id} takes precedence
                  if (ticket?.event_location_revealed && ticket.event_location) return ticket.event_location;
                  if (event.location) return event.location;
                  if (event.location_visibility === 'revealed_later') return 'Location will be shared before the event';
                  if (event.location_visibility === 'hidden') return 'Location is private';
                  return 'TBA';
                })()}
              </div>
            </div>
          </div>

          {/* Add-Ons */}
          {hasAddOns && (
            <div className="px-5 py-4 border-b border-[#1A1A1A]" data-testid="addons-section">
              <div className="text-[9px] tracking-[0.3em] text-[#C6A66B] uppercase font-semibold text-center mb-2">Included Extras</div>
              <div className="space-y-1.5">
                {ticket.purchased_add_ons.map((ao) => (
                  <div key={ao.add_on_id || ao.name} className="flex justify-between text-sm px-2">
                    <span className="text-[#B9B1A6]">{ao.name}{ao.quantity > 1 ? ` x${ao.quantity}` : ''}</span>
                    <span className="text-[#F3EEE7] font-medium">${(ao.price * (ao.quantity || 1)).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="px-5 py-4 bg-[#0D0D0D] text-center" data-testid="total-section">
            <div className="text-[9px] tracking-[0.3em] text-[#777] uppercase font-semibold" data-testid="total-label">{paymentTotalLabel(ticket)}</div>
            <div className="text-2xl font-black text-[#C6A66B] mt-1" data-testid="total-amount">
              {isCashRsvp(ticket) && !isCashCollected(ticket)
                ? `$${(ticket.expected_amount || ticket.amount_paid || 0).toFixed(2)}`
                : `$${ticket.amount_paid?.toFixed(2) || '0.00'}`}
            </div>
            {isCashRsvp(ticket) && !isCashCollected(ticket) && (
              <div className="text-[10px] text-[#AFAFAF] mt-1" data-testid="cash-due-note">Pay the host at check-in</div>
            )}
            {isCashCollected(ticket) && (
              <div className="text-[10px] text-emerald-400 mt-1" data-testid="cash-collected-note">Payment received — thank you</div>
            )}
          </div>

          {/* Secondary details — reduced visual weight */}
          <div className="px-5 py-3 bg-[#080808] border-t border-[#1A1A1A] text-center space-y-0.5">
            <div className="text-[11px] text-[#555]" data-testid="guest-email">{ticket.guest_email}</div>
            {ticket.payment_confirmation && (
              <div className="text-[11px] text-[#444] font-mono">Ref: {ticket.payment_confirmation}</div>
            )}
            {ticket.payment_method_used && (
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#444]">
                <PaymentProviderLogo provider={getProviderFromMethod(ticket.payment_method_used)} method={ticket.payment_method_used} size="sm" />
                <span>via {ticket.payment_method_used}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── ACTIONS (granted, non-demo) ── */}
        {isGranted && !showDemoPanel && (
          <div className="space-y-3 mb-10" data-testid="pass-actions">
            <a
              href={`${API}/tickets/${ticket_id}/pdf-download`}
              download
              className="flex items-center justify-center w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] px-6 py-4 rounded-sm font-bold text-sm transition-opacity"
              data-testid="download-pass-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Pass (PDF)
            </a>

            <button
              onClick={() => downloadICS(event)}
              className="flex items-center justify-center w-full border border-[#C6A66B]/30 bg-transparent hover:bg-[#C6A66B]/5 text-[#C6A66B] px-6 py-3.5 rounded-sm font-semibold text-sm transition-colors"
              data-testid="add-to-calendar-button"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Add to Calendar
            </button>

            {(walletStatus.apple_wallet || walletStatus.google_wallet) && (
              <div className="space-y-3">
                {walletStatus.apple_wallet && (
                  <button onClick={handleAppleWallet} disabled={walletLoading === 'apple'} className="flex items-center justify-center w-full border border-[#EAE7E1] bg-[#EAE7E1] text-[#050505] hover:bg-white rounded-sm px-4 py-3.5 font-semibold text-xs transition-all" data-testid="apple-wallet-button">
                    <Smartphone className="w-3.5 h-3.5 mr-2" />{walletLoading === 'apple' ? 'Generating...' : 'Add to Apple Wallet'}
                  </button>
                )}
                {walletStatus.google_wallet && (
                  <button onClick={handleGoogleWallet} disabled={walletLoading === 'google'} className="flex items-center justify-center w-full border border-[#4285F4] bg-[#4285F4] text-white hover:bg-[#3367D6] rounded-sm px-4 py-3.5 font-semibold text-xs transition-all" data-testid="google-wallet-button">
                    <Smartphone className="w-3.5 h-3.5 mr-2" />{walletLoading === 'google' ? 'Generating...' : 'Add to Google Wallet'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── PENDING ACTIONS ── */}
        {isPending && (
          <div className="space-y-3 mb-10" data-testid="pending-actions">
            <button
              onClick={() => downloadICS(event)}
              className="flex items-center justify-center w-full border border-[#FFB800]/30 bg-transparent hover:bg-[#FFB800]/5 text-[#FFB800] px-6 py-3.5 rounded-sm font-semibold text-sm transition-colors"
              data-testid="add-to-calendar-button"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Add to Calendar
            </button>
            <p className="text-xs text-[#777] text-center">You'll receive an email once your access is approved.</p>
          </div>
        )}

        {/* ── CHECK-IN INFO (non-demo) ── */}
        {(isGranted || isPending) && !showDemoPanel && (
          <div className="border border-[#1A1A1A] rounded-md p-5 mb-10 bg-[#0A0A0A]" data-testid="checkin-info">
            <div className="text-[9px] tracking-[0.3em] text-[#C6A66B] uppercase font-semibold mb-4 text-center">At Check-In</div>
            <ol className="space-y-3">
              <li className="flex items-start gap-3 text-sm">
                <span className="w-5 h-5 rounded-full bg-[#C6A66B]/10 text-[#C6A66B] flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">1</span>
                <span className="text-[#B9B1A6]">Proceed to the check-in area</span>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <span className="w-5 h-5 rounded-full bg-[#C6A66B]/10 text-[#C6A66B] flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">2</span>
                <span className="text-[#B9B1A6]">{isGranted ? 'Show your QR code' : 'Show this page to the host'}</span>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <span className="w-5 h-5 rounded-full bg-[#C6A66B]/10 text-[#C6A66B] flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">3</span>
                <span className="text-[#B9B1A6]">Staff will scan and check you in</span>
              </li>
            </ol>
            <p className="text-[11px] text-[#555] text-center mt-4 italic" data-testid="screenshot-tip">
              Tip: Take a screenshot for fastest entry
            </p>
          </div>
        )}

        {/* ── POST-DEMO CONVERSION ── */}
        {showDemoPanel && (
          <div className="mb-10" data-testid="demo-conversion-panel">
            <div className="border border-[#C6A66B]/30 rounded-md overflow-hidden bg-gradient-to-b from-[#0D0D0D] to-[#080808]">
              <div className="h-px bg-gradient-to-r from-transparent via-[#C6A66B] to-transparent" />
              <div className="px-5 py-8 text-center">
                <div className="text-[9px] tracking-[0.35em] text-[#C6A66B]/60 font-semibold uppercase mb-4">What's Next</div>
                <h2 className="text-xl font-black text-[#F3EEE7] tracking-tight mb-1" data-testid="demo-conversion-headline">
                  You just ran your first event.
                </h2>
                <p className="text-base text-[#C6A66B] font-medium mb-6" data-testid="demo-conversion-subheadline">
                  Now create your own in minutes.
                </p>
                <div className="flex flex-col items-center gap-2 mb-7">
                  {[
                    { icon: Zap, text: 'No setup required' },
                    { icon: CreditCard, text: 'Works with Zelle, Venmo, CashApp' },
                    { icon: Sparkles, text: 'Be live in minutes' },
                  ].map(({ icon: Icon, text }) => (
                    <span key={text} className="flex items-center gap-1.5 text-xs text-[#B9B1A6]">
                      <Icon className="w-3 h-3 text-[#C6A66B]" />
                      {text}
                    </span>
                  ))}
                </div>
                <button
                  onClick={handleCreateEvent}
                  className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] px-6 py-4 rounded-sm font-bold text-sm transition-opacity flex items-center justify-center gap-2"
                  data-testid="demo-create-event-button"
                >
                  {isLoggedIn ? 'Create Your Event' : 'Sign Up to Create Events'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate(isLoggedIn ? '/setup' : '/login')}
                  className="w-full mt-2 border border-[#C6A66B]/25 hover:bg-[#C6A66B]/5 text-[#C6A66B] px-6 py-3.5 rounded-sm text-sm font-medium transition-colors"
                  data-testid="demo-setup-defaults-button"
                >
                  {isLoggedIn ? 'Set Up My Event Defaults' : 'Sign In'}
                </button>
                <p className="text-xs text-[#777] mt-3">Save your common setup once so future events take minutes</p>
              </div>
            </div>
          </div>
        )}

        {/* ── FOOTER ── */}
        <div className="text-center pt-6 pb-4 border-t border-[#1A1A1A]">
          {platformSettings?.platform_logo_url ? (
            <div>
              <div className="text-[9px] text-[#444] mb-2 uppercase tracking-[0.25em]">Powered by</div>
              <SafeImage src={`${BACKEND_URL}${platformSettings.platform_logo_url}`} alt="VelvetGate" className="h-6 mx-auto" fallbackLabel="VG" fallbackClassName="h-6 w-12 mx-auto" data-testid="platform-logo-footer" />
            </div>
          ) : (
            <div>
              <div className="text-[9px] text-[#444] mb-1 uppercase tracking-[0.25em]">Powered by</div>
              <div className="text-sm font-bold text-[#C6A66B]/50 tracking-tight">VelvetGate</div>
            </div>
          )}
          <div className="flex items-center justify-center gap-4 mt-4 text-[11px] text-[#555]" data-testid="ticket-help-links">
            <a href="/faq" className="hover:text-[#D4AF37] transition-colors" data-testid="ticket-faq-link">Questions? View FAQ</a>
            <span className="text-[#2A2A2A]">·</span>
            <a href="/contact" className="hover:text-[#D4AF37] transition-colors" data-testid="ticket-contact-link">Need help? Contact us</a>
          </div>
          <div className="mt-2 text-[11px] text-[#555]">
            <a href="/find-pass" className="hover:text-[#D4AF37] transition-colors" data-testid="ticket-find-pass-link">Lost your link? Find My Pass</a>
          </div>
        </div>

      </div>
    </div>
  );
}
